import React from 'react';
import { ArrowLeft, Camera, Star, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import { useNavigate } from 'react-router-dom';
import { useLanguage, getPageTexts } from '../../i18n/pageTranslations';

const ResultCard = ({ title, description, imageUrl, link }) => {
  const navigate = useNavigate();
  
  return (
    <Card 
      onClick={() => navigate(link)}
      className="relative h-72 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-500 border-none rounded-none"
    >
      <div className="absolute inset-0">
        <img 
          src={imageUrl} 
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#3d5a6f]/70 via-[#3d5a6f]/60 to-[#1C1C1A]/80"></div>
      </div>
      <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-0.5 bg-ivory/60 mb-4"></div>
        <h4 className="text-lg md:text-xl font-light text-white uppercase tracking-wide leading-tight mb-2">
          {title}
        </h4>
        <p className="text-white/80 text-sm">{description}</p>
        <div className="w-16 h-0.5 bg-ivory/60 mt-4"></div>
      </div>
    </Card>
  );
};

const Resultados = () => {
  const navigate = useNavigate();
  const lang = useLanguage();
  const t = getPageTexts('rinoResultados', lang);

  // Casos con imágenes reales de rinomodelacion.com
  const casos = [
    {
      title: 'Corrección de Rinoplastia Previa',
      description: 'Casos de retoque post-quirúrgico',
      image: 'https://www.rinomodelacion.com/s/cc_images/teaserbox_29765135.jpg?t=1521738840',
      link: '/rinomodelacion/resultados/correccion-rinoplastia'
    },
    {
      title: 'Rinomodelación Caso 2',
      description: 'Corrección de giba nasal',
      image: 'https://www.rinomodelacion.com/s/cc_images/teaserbox_29765138.jpg?t=1496397202',
      link: '/rinomodelacion/resultados/ejemplo-2'
    },
    {
      title: 'Rinomodelación Caso 3',
      description: 'Resultados naturales sin cirugía',
      image: 'https://www.rinomodelacion.com/s/cc_images/teaserbox_29765140.jpg?t=1496397203',
      link: '/rinomodelacion/resultados/ejemplo-3'
    },
    {
      title: 'Rinomodelación Caso 4',
      description: 'Resultado inmediato',
      image: 'https://www.rinomodelacion.com/s/cc_images/teaserbox_29765143.jpg?t=1496397205',
      link: '/rinomodelacion/resultados/ejemplo-4'
    },
    {
      title: 'Perfiloplastia Completa',
      description: 'Mentoplastia + Rinomodelación',
      image: 'https://www.rinomodelacion.com/s/cc_images/teaserbox_29765145.jpg?t=1496397206',
      link: '/rinomodelacion/resultados/perfiloplastia'
    },
    {
      title: 'Rinomodelación Caso 6',
      description: 'Estética nasal sin cirugía',
      image: 'https://www.rinomodelacion.com/s/cc_images/teaserbox_29765147.jpg?t=1496397206',
      link: '/rinomodelacion/resultados/ejemplo-6'
    },
    {
      title: 'Rinomodelación Caso 7',
      description: 'Punta nasal levantada',
      image: 'https://www.rinomodelacion.com/s/cc_images/teaserbox_29765149.jpg?t=1496397207',
      link: '/rinomodelacion/resultados/ejemplo-7'
    },
    {
      title: 'Rinomodelación Caso 8',
      description: 'Corrección de dorso y punta',
      image: 'https://www.rinomodelacion.com/s/cc_images/teaserbox_29765153.jpg?t=1496397208',
      link: '/rinomodelacion/resultados/ejemplo-8'
    },
    {
      title: 'Rinomodelación Caso 9',
      description: 'Perfil armonizado',
      image: '/images/rinomodelacion-caso-nuevo-post.jpg',
      link: '/rinomodelacion/resultados/ejemplo-9'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.back}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-text-secondary">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 px-4 bg-ivory">
        <div className="container mx-auto max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <Camera className="text-gold mx-auto mb-4" size={48} />
              <div className="text-4xl font-light text-[#1C1C1A] mb-2">{t.cases}</div>
              <p className="text-text-secondary">{t.casesLabel}</p>
            </div>
            <div className="p-6">
              <Star className="text-gold mx-auto mb-4" size={48} />
              <div className="text-4xl font-light text-[#1C1C1A] mb-2">{t.sat}</div>
              <p className="text-text-secondary">{t.satLabel}</p>
            </div>
            <div className="p-6">
              <Users className="text-gold mx-auto mb-4" size={48} />
              <div className="text-4xl font-light text-[#1C1C1A] mb-2">{t.exp}</div>
              <p className="text-text-secondary">{t.expLabel}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Note about photos */}
      <section className="py-4 px-4 bg-cream">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-sm text-text-secondary italic">
            Las fotografías muestran personas reales antes y después del tratamiento realizado por el Dr. Ángel Ruiz Norro. 
            Han autorizado por escrito el que sean mostradas. Las fotos no han sido retocadas ni procesadas.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-8 px-4 bg-cream">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-r from-cream0 to-blue-500 text-white p-8 rounded-none text-center">
            <h3 className="text-2xl font-light mb-4">Casos Reales Documentados</h3>
            <p className="text-lg mb-6">
              Cada caso es único. Durante su consulta personalizada, el Dr. Norro le mostrará casos similares al tuyo para que puedas tener expectativas realistas.
            </p>
            <Button 
              onClick={() => navigate('/rinomodelacion/contacto')}
              className="bg-ivory text-gold hover:bg-gray-100 px-8 py-3"
            >
              Ver mi caso personalizado
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-light text-[#1C1C1A] mb-12 text-center">
            Galería de Resultados
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {casos.map((caso, index) => (
              <ResultCard 
                key={index}
                title={caso.title}
                description={caso.description}
                imageUrl={caso.image}
                link={caso.link}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-[#1C1C1A]">
        <div className="container mx-auto max-w-3xl text-center text-white">
          <h2 className="text-3xl font-light mb-6">{t.ctaTitle}</h2>
          <p className="text-lg mb-8 text-white/80">
            {t.ctaDesc}
          </p>
          <Button 
            onClick={() => navigate('/rinomodelacion/contacto')}
            className="bg-gold hover:bg-gold text-white px-8 py-6 text-lg rounded-none"
          >
            {t.ctaButton}
          </Button>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Resultados;
